import get from "lodash/get";

import Tokens from "./semantic";
import {
  ActionBorderColorToken,
  ActionColorToken,
  ActionIconColorToken,
  ActionSurfaceColorToken,
  ActionTextColorToken,
  BorderColorToken,
  ColorToken,
  CoreBorderColorToken,
  CoreColorToken,
  CoreIconColorToken,
  CoreSurfaceColorToken,
  CoreTextColorToken,
  ExpressiveColorToken,
  ExpressiveIconColorToken,
  ExpressiveSurfaceColorToken,
  ExpressiveTextColorToken,
  IconColorToken,
  MessageBorderColorToken,
  MessageColorToken,
  MessageIconColorToken,
  MessageSurfaceColorToken,
  MessageTextColorToken,
  ProgramColorToken,
  ProgramIconColorToken,
  ProgramSurfaceColorToken,
  ProgramTextColorToken,
  SurfaceColorToken,
  TextColorToken,
} from "./types";

export const getColorTokenValue = <T extends ColorToken>(token: T) =>
  get(Tokens, token);

export const isBorderColorToken = (
  token: ColorToken
): token is BorderColorToken => token.includes(".border.");

export const isIconColorToken = (token: ColorToken): token is IconColorToken =>
  token.includes(".icon.");

export const isSurfaceColorToken = (
  token: ColorToken
): token is SurfaceColorToken => token.includes(".surface.");

export const isTextColorToken = (token: ColorToken): token is TextColorToken =>
  token.includes(".text.");

export const isActionColorToken = (
  token: ColorToken
): token is ActionColorToken => token.startsWith("Action.");

export const isActionBorderColorToken = (
  token: ColorToken
): token is ActionBorderColorToken =>
  isActionColorToken(token) && isBorderColorToken(token);

export const isActionIconColorToken = (
  token: ColorToken
): token is ActionIconColorToken =>
  isActionColorToken(token) && isIconColorToken(token);

export const isActionSurfaceColorToken = (
  token: ColorToken
): token is ActionSurfaceColorToken =>
  isActionColorToken(token) && isSurfaceColorToken(token);

export const isActionTextColorToken = (
  token: ColorToken
): token is ActionTextColorToken =>
  isActionColorToken(token) && isTextColorToken(token);

export const isCoreColorToken = (token: ColorToken): token is CoreColorToken =>
  token.startsWith("Core.");

export const isCoreBorderColorToken = (
  token: ColorToken
): token is CoreBorderColorToken =>
  isCoreColorToken(token) && isBorderColorToken(token);

export const isCoreIconColorToken = (
  token: ColorToken
): token is CoreIconColorToken =>
  isCoreColorToken(token) && isIconColorToken(token);

export const isCoreSurfaceColorToken = (
  token: ColorToken
): token is CoreSurfaceColorToken =>
  isCoreColorToken(token) && isSurfaceColorToken(token);

export const isCoreTextColorToken = (
  token: ColorToken
): token is CoreTextColorToken =>
  isCoreColorToken(token) && isTextColorToken(token);

export const isExpressiveColorToken = (
  token: ColorToken
): token is ExpressiveColorToken => token.startsWith("Expressive.");

export const isExpressiveIconColorToken = (
  token: ColorToken
): token is ExpressiveIconColorToken =>
  isExpressiveColorToken(token) && isIconColorToken(token);

export const isExpressiveSurfaceColorToken = (
  token: ColorToken
): token is ExpressiveSurfaceColorToken =>
  isExpressiveColorToken(token) && isSurfaceColorToken(token);

export const isExpressiveTextColorToken = (
  token: ColorToken
): token is ExpressiveTextColorToken =>
  isExpressiveColorToken(token) && isTextColorToken(token);

export const isMessageColorToken = (
  token: ColorToken
): token is MessageColorToken => token.startsWith("Message.");

export const isMessageBorderColorToken = (
  token: ColorToken
): token is MessageBorderColorToken =>
  isMessageColorToken(token) && isBorderColorToken(token);

export const isMessageIconColorToken = (
  token: ColorToken
): token is MessageIconColorToken =>
  isMessageColorToken(token) && isIconColorToken(token);

export const isMessageSurfaceColorToken = (
  token: ColorToken
): token is MessageSurfaceColorToken =>
  isMessageColorToken(token) && isSurfaceColorToken(token);

export const isMessageTextColorToken = (
  token: ColorToken
): token is MessageTextColorToken =>
  isMessageColorToken(token) && isTextColorToken(token);

export const isProgramColorToken = (
  token: ColorToken
): token is ProgramColorToken => token.startsWith("Program.");

export const isProgramIconColorToken = (
  token: ColorToken
): token is ProgramIconColorToken =>
  isProgramColorToken(token) && isIconColorToken(token);

export const isProgramSurfaceColorToken = (
  token: ColorToken
): token is ProgramSurfaceColorToken =>
  isProgramColorToken(token) && isSurfaceColorToken(token);

export const isProgramTextColorToken = (
  token: ColorToken
): token is ProgramTextColorToken =>
  isProgramColorToken(token) && isTextColorToken(token);
